import React, { FC, useContext, useMemo, useState } from 'react';

// components
import IconButton from '../../../../components/buttons/iconButton';
import RenderHTML from '../../../../components/shared/RenderHTML';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaType } from '../../../../constants/formulaType';
import ICONS from '../../../../constants/icons';

//context
import { EditorContext } from '../../../../context/EditorContext';

// hooks
import useQueryEmailTemplate from '../../../../hooks/EmailTemplate/useQueryEmailTemplate';
import useCustomEmailTemplateDetail from '../../../../hooks/custom-email-templates/useCustomEmailTemplateDetail';

// models
import {
    EmailPreviewModel,
    SectionType,
} from '../../../../models/editor.model';
import { PreviewComponentProps } from '../../../../models/formula.model';

//helpers
import { updateTemplateForPreview } from './email.helper';

interface StyleProps {
    isMobile?: boolean;
}
interface EmailField {
    [key: string]: string;
}

const EmailMain: FC<
    React.PropsWithChildren<PreviewComponentProps<EmailPreviewModel>>
> = ({ randomSample, isCoverPreview }) => {
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(0);
    const currentPreview = randomSample[currentPreviewIndex]?.content;
    const { editorDetails } = useContext(EditorContext);

    const emailTemplateId = useMemo<string | undefined>(() => {
        return editorDetails?.formula?.additional_metadata?.email_template_id;
    }, [editorDetails]);

    const isAampeCustomEmailTemplate = useMemo<boolean | undefined>(() => {
        return !!editorDetails?.formula?.additional_metadata
            ?.is_aampe_custom_template;
    }, [editorDetails]);

    const { data: templateData } = useQueryEmailTemplate(
        emailTemplateId as string,
        editorDetails.formulaType as FormulaType,
        !isAampeCustomEmailTemplate,
    );

    const { data: customTemplateData } = useCustomEmailTemplateDetail(
        emailTemplateId as string,
        isAampeCustomEmailTemplate,
    );

    const htmlContent = useMemo<string>(() => {
        if (isAampeCustomEmailTemplate)
            return customTemplateData?.html_content ?? '';
        return templateData?.htmlContent ?? '';
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const dynamicEmailFields = useMemo<string[]>(() => {
        if (isAampeCustomEmailTemplate) {
            return customTemplateData?.additional_metadata?.dynamic_keys ?? [];
        }
        return templateData?.dynamicFieldsWithModifiers as string[];
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const emailFieldsWithData = useMemo<EmailField>(() => {
        if (currentPreview) {
            return currentPreview;
        } else if (dynamicEmailFields) {
            return Object.fromEntries(
                dynamicEmailFields.map((entry) => [entry, '']),
            );
        } else return {} as EmailField;
    }, [currentPreview, dynamicEmailFields]);

    const updatedTemplate = useMemo<string>(() => {
        return updateTemplateForPreview(
            htmlContent as string,
            emailFieldsWithData,
        );
    }, [emailFieldsWithData, htmlContent]);

    const handleLeftClick = () => {
        if (currentPreviewIndex > 0) {
            setCurrentPreviewIndex((prev) => prev - 1);
        }
    };

    const handleRightClick = () => {
        if (currentPreviewIndex < randomSample.length - 1) {
            setCurrentPreviewIndex((prev) => prev + 1);
        }
    };

    return (
        <Container>
            <ScreenSizeContainer isMobile={editorDetails.isSecondaryView}>
                {randomSample.length > 1 && (
                    <PaginationContainer>
                        <IconButton
                            Icon={ICONS.leftChevron}
                            action={handleLeftClick}
                            disabled={currentPreviewIndex === 0}
                        />
                        {isCoverPreview ? (
                            <span>
                                {' '}
                                {currentPreviewIndex + 1} /{' '}
                                {randomSample.length}
                            </span>
                        ) : (
                            <ICONS.ellipses />
                        )}
                        <IconButton
                            Icon={ICONS.rightChevron}
                            action={handleRightClick}
                            disabled={
                                currentPreviewIndex === randomSample.length - 1
                            }
                            color={aampeTheme.colors.solidA01}
                        />
                    </PaginationContainer>
                )}
                {Object.keys(emailFieldsWithData).includes(
                    SectionType.subject,
                ) && `Subject: ${emailFieldsWithData[SectionType.subject]}`}
                <RenderHTML htmlString={updatedTemplate ?? ''} />
            </ScreenSizeContainer>
        </Container>
    );
};

export default EmailMain;

const Container = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing(3),
    height: '100%',
    width: '100%',
});
const PaginationContainer = styled.div({
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    gap: aampeTheme.spacing(1),
    width: '100%',
    marginTop: aampeTheme.spacing4(1),
    marginBottom: aampeTheme.spacing4(4),
});

const ScreenSizeContainer = styled.div<StyleProps>(({ isMobile }) => ({
    border: `${aampeTheme.colors.bordersWG01} 1px solid`,
    borderRadius: aampeTheme.borderRadius.default,
    height: '100%',
    padding: aampeTheme.spacing(1, 2),
    display: 'flex',
    flexDirection: 'column',
    width: isMobile ? '53%' : '100%',
}));
